import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { I18NextPipe } from 'angular-i18next';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { usersService } from 'src/app/services/services/users.service';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';
import { DialogDeleteNewComponent } from 'src/app/common/dialogs/dialog-delete-new/dialog-delete-new.component';
import { NGXLogger } from 'ngx-logger';
import { finalize } from 'rxjs';
import { Store } from '@ngrx/store';
import * as mainActions from 'src/app/state/app.actions';
import { InfoMessage } from 'src/app/common/messages';
import { mainState } from 'src/app/state/app.reducer';

export interface FiltersetRow {
  filtersetHeaderId: number;
  description: string;
  inUse: boolean;
  isNew?: boolean;
  isEditing?: boolean;
  editName?: string;
  isTouched?: boolean;
}

@Component({
    selector: 'app-users-filter',
    templateUrl: './usersFilter.component.html',
    styleUrls: ['./usersFilter.component.scss'],
    standalone: false
})
export class UsersFilterComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  BreadcrumbsLink = [
    { label: 'Home', url: '/' },
    { label:  this.i18n.transform('users:users_tooltip_filterHeader'), url: '/users/users-filter' }
  ];

  displayedColumns: string[] = ['name', 'edit', 'delete'];
  dataSource = new MatTableDataSource<FiltersetRow>([]);

  filter = '';
  isLoading = true;
  hasChanges = false;
  pendingDeletes: string[] = [];

  // Snapshot for undo
  private snapshot: FiltersetRow[] = [];

  constructor(
    private i18n: I18NextPipe,
    private dialog: MatDialog,
    private router: Router,
    private usersService: usersService,
    private logger: NGXLogger,
    private store: Store<mainState>
  ) { }

  ngOnInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (row: FiltersetRow, column: string) => {
      switch (column) {
        case 'name': return (row.description || '').toLowerCase();
        default: return '';
      }
    };

    this.dataSource.filterPredicate = (row: FiltersetRow, filterText: string) => {
      const f = (filterText || '').trim().toLowerCase();
      if (!f) return true;
      return (row.description || '').toLowerCase().includes(f);
    };

    this.loadData();
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  private loadData(): void {
    this.isLoading = true;
    this.usersService.getFiltersetList()
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (result) => {
          if (result && result.filtersets) {
            const rows: FiltersetRow[] = result.filtersets.map((f: any) => ({
              filtersetHeaderId: f.filtersetHeaderId,
              description: f.description,
              inUse: f.inUse,
              isNew: false,
              isEditing: false,
              isTouched: false
            }));
            this.dataSource.data = rows;
            this.snapshot = JSON.parse(JSON.stringify(rows));
            this.updateChangesState();
          }
        },
        error: (error) => {
          this.logger.error('Error loading filtersets:', error);
        }
      });
  }

  applyFilterFromProperty(): void {
    this.dataSource.filter = (this.filter || '').trim().toLowerCase();
  }

  clearFilter(): void {
    this.filter = '';
    this.dataSource.filter = '';
    this.applyFilterFromProperty();
  }

  openHelp(): void {
    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: 'backdrop-invisible',
      width: '600px',
      data: {
        title: this.i18n.transform('users:help_title_usersfilter'),
        text: this.i18n.transform('users:help_usersfilter'),
        renderAsHtml: true
      }
    });
  }

  addFilter(): void {
    const newRow: FiltersetRow = {
      filtersetHeaderId: 0,
      description: '',
      inUse: false,
      isNew: true,
      isEditing: true,
      editName: '',
      isTouched: false
    };
    this.dataSource.data = [newRow, ...this.dataSource.data];
    this.clearFilter();
    this.updateChangesState();
  }

  editFilter(row: FiltersetRow): void {
    if( row.isEditing) {
      if(this.validateFiltersetName(row.editName)){
        return;
      }
      row.isEditing = false;
      row.isTouched = false;
      return;
    }
    row.isEditing = true;
    row.editName = row.description;
    row.isTouched = false;
    this.updateChangesState();
  }

  onEdit(row: FiltersetRow): void {
    // Mark as touched when user interacts with the field
    row.isTouched = true;
    row.description = row.editName.trim();
    this.updateChangesState();
  }

  cancelEdit(row: FiltersetRow): void {
    if (row.isNew) {
      // Remove new row
      this.dataSource.data = this.dataSource.data.filter(r => r !== row);
    } else {
      row.isEditing = false;
      row.editName = row.description;
      row.isTouched = false;
    }
    this.updateChangesState();
  }

  deleteFilter(row: FiltersetRow): void {
    if (row.inUse) {
      return; // Cannot delete if in use
    }

    const title = this.i18n.transform('users:usersfilter_deleteFilterTitle') || 'Delete filter';
    let message = this.i18n.transform('users:usersfilter_deleteFilterMessage');
    if (message && message.includes('{{filterName}}')) {
      message = message.replace('{{filterName}}', row.description);
    } else {
      message = message || `Are you sure you want to delete the filter "${row.description}"?`;
    }

    const dialogRef = this.dialog.open(DialogDeleteNewComponent, {
      disableClose: true,
      data: {
        title: title,
        showTitleInHeader: true,
        additionalText: message,
        sizeOption: 'small',
      }
    });

    dialogRef.afterClosed().subscribe(confirm => {
      if (!confirm) return;

      // If it's a new row that hasn't been saved, just remove it from the list
      if (row.isNew || row.filtersetHeaderId === 0) {
        this.dataSource.data = this.dataSource.data.filter(r => r !== row);
        this.updateChangesState();
        return;
      }

      // For existing records, send delete request to server immediately
      this.isLoading = true;
      const deletePayload = {
        added: [],
        deletes: [row.filtersetHeaderId.toString()],
        changes: []
      };

      this.usersService.updateFiltersetList(deletePayload)
        .pipe(finalize(() => this.isLoading = false))
        .subscribe({
          next: (result) => {
            if (result && result.errorCount === 0) {
              // Success - remove from dataSource and snapshot to preserve pending changes
              this.dataSource.data = this.dataSource.data.filter(r => r !== row);
              // Also remove from snapshot so it doesn't appear in pending deletes
              this.snapshot = this.snapshot.filter(s => s.filtersetHeaderId !== row.filtersetHeaderId);
              this.updateChangesState();
            } else {
              // Handle errors
              this.logger.error('Error deleting filterset:', result);
              // Optionally show error message to user
            }
          },
          error: (error) => {
            this.logger.error('Error deleting filterset:', error);
            // Optionally show error message to user
          }
        });
    });
  }

  navigateToFilter(row: FiltersetRow): void {
    if (row.filtersetHeaderId > 0) {
      this.router.navigate(['/users/users-filter/get', row.filtersetHeaderId]);
    }
  }

  saveChanges(): void {
    const diff = this.calculateDiff();
    const changeCount = diff.added.length + diff.deletes.length + diff.changes.length;

    if (changeCount === 0) {
      return;
    }

    // Validate all names
    const invalidNames = [...diff.added, ...diff.changes].filter(item =>
      this.validateFiltersetName(item.name)
    );

    if (invalidNames.length > 0) {
      // Show error
      return;
    }

    this.isLoading = true;
    this.usersService.updateFiltersetList(diff)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (result) => {
          if (result && result.errorCount === 0) {
            // Success - reload data
            this.loadData();
            this.pendingDeletes = [];
            // Immediately update state to disable save button
            this.hasChanges = false;
            // Show success message
            this.store.dispatch(
              mainActions.addMessage({
                message: new InfoMessage(this.i18n.transform('users:shared_err_ok'), '')
              })
            );
          } else {
            // Handle errors
            this.logger.error('Error saving filtersets:', result);
          }
        },
        error: (error) => {
          this.logger.error('Error saving filtersets:', error);
        }
      });
  }

  undoChanges(): void {
    this.dataSource.data = JSON.parse(JSON.stringify(this.snapshot));
    this.pendingDeletes = [];
    this.updateChangesState();
  }

  private calculateDiff(): any {
    const current = this.dataSource.data;
    const added = current.filter(r => r.isNew && r.description.trim() !== '')
      .map(r => ({ name: r.description }));

    const deletes = this.snapshot
      .filter(s => !current.find(c => c.filtersetHeaderId === s.filtersetHeaderId && !c.isNew))
      .map(s => s.filtersetHeaderId.toString());

    const changes = current
      .filter(r => !r.isNew && r.filtersetHeaderId > 0)
      .filter(r => {
        const original = this.snapshot.find(s => s.filtersetHeaderId === r.filtersetHeaderId);
        return original && original.description !== r.description;
      })
      .map(r => ({ id: r.filtersetHeaderId.toString(), name: r.description }));

    return { added, deletes, changes };
  }

  private updateChangesState(): void {
    const diff = this.calculateDiff();
    this.hasChanges = diff.added.length > 0 || diff.deletes.length > 0 || diff.changes.length > 0;
  }

  private validateFiltersetName(name: string): boolean {
    if( !name || name.trim() === '') {
      return true;
    }
    return /["§]/.test(name);
  }

  isInvalidName(name: string): boolean {
    return this.validateFiltersetName(name);
  }

  hasInvalidInputs(): boolean {
    return this.dataSource.data.some(row => {
      if (row.isEditing || row.isNew) {
        // Only check for invalid inputs if the field has been touched
        return row.isTouched && this.isInvalidName(row.editName || '');
      }
      return false;
    });
  }
}
